import { FunctionComponent, useState, useEffect } from 'react'

import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import Modal from 'react-modal'

import { Container, SelectPicker } from 'rsuite'
import styled from 'styled-components'

import { Text } from 'theme-ui'

import { baseColors, Button } from '@fairhq/common'

import { CharlieUser } from 'auth/types'
import { useAppSelector } from 'store/hooks'
import { State } from 'store/state'
import {
  useGetTasksFollowUpQuestionsQuery,
  usePostTaskAnswerMutation,
} from 'store/tasks/tasksApi'
import { Task as TaskType } from 'store/tasks/types'
import WhiteBox from 'ui-kit/WhiteBox'
import { insightsTaskRejectedEvent } from 'utils/analytics/amplitudeEvents'

export const ChoiceContainer = styled(Container)`
  .rs-radio,
  .rs-checkbox {
    &:not(:last-of-type) {
      margin-right: 0;
      margin-bottom: 12px;
    }

    label {
      text-align: left;
      white-space: initial;
    }
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 20px 0;
  gap: 10px;
`

const ErrorContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

interface SkippedTaskModalProps {
  task: TaskType
  onClose: () => void
  charlieUser: CharlieUser
}

export const SkippedTaskModal: FunctionComponent<SkippedTaskModalProps> = ({
  task,
  onClose,
  charlieUser,
}) => {
  const { t } = useTranslation()
  const [otherInput, setOtherInput] = useState('')
  const [selectedOption, setSelectedOption] = useState(null)

  const sessionId = useAppSelector(
    (state: State) => state.apiHeadersReducer.sessionId
  )

  const { data: questionsData, isSuccess: isQuestionsSuccess } =
    useGetTasksFollowUpQuestionsQuery({ sessionId })
  const [
    postTaskAnswer,
    { isError: isAnswerError, isSuccess: isAnswerSuccess },
  ] = usePostTaskAnswerMutation()

  useEffect(() => {
    if (isAnswerSuccess) {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnswerSuccess])

  if (!isQuestionsSuccess) {
    return null
  }
  const firstQuestion = questionsData[0]
  const { options } = firstQuestion

  const isOtherVisible = options?.find(
    ({ id, isOther }) => id === selectedOption && isOther
  )

  return (
    <Modal isOpen onRequestClose={onClose} shouldCloseOnOverlayClick>
      <WhiteBox
        sx={{
          width: '100%',
          padding: '48px',
        }}
      >
        <Text as="div" variant="subtitle" sx={{ textAlign: 'center', mb: 2 }}>
          {t(`tasksQuestions.${firstQuestion.code}.modalTitle`)}
        </Text>
        <Text as="div" variant="h3" sx={{ mb: 1 }}>
          {task.description}
        </Text>
        <Text as="div" sx={{ mb: 5 }}>
          {firstQuestion.description}
        </Text>
        <Text as="div" variant="label" sx={{ mb: 1, fontWeight: 'bold' }}>
          {firstQuestion.title}
        </Text>

        <ChoiceContainer>
          <SelectPicker
            onChange={value => setSelectedOption(value)}
            value={selectedOption}
            data={
              _.sortBy(options, ['priority'])?.map(option => ({
                value: option.id,
                label: option.title,
              })) ?? []
            }
            placeholder={t(`tasksQuestions.PickReason`)}
            searchable={false}
            maxHeight={300}
            size="lg"
          />

          {isOtherVisible ? (
            <textarea
              style={{ marginTop: '12px' }}
              onChange={e => setOtherInput(e.target.value)}
              rows={3}
              value={otherInput}
            />
          ) : undefined}
        </ChoiceContainer>

        <ButtonsContainer>
          <Button
            sx={{ mr: 2 }}
            disabled={!selectedOption || (isOtherVisible && !otherInput)}
            onClick={() => {
              if (selectedOption) {
                postTaskAnswer({
                  questionId: firstQuestion.id,
                  taskId: task.id,
                  optionId: selectedOption,
                  otherInput,
                  sessionId,
                })
                insightsTaskRejectedEvent(
                  { taskId: task.id, optionId: selectedOption },
                  charlieUser
                )
              }
            }}
          >
            {t(`tasksQuestions.${firstQuestion.code}.modalConfirm`)}
          </Button>
          <Button variant="tertiary" onClick={onClose}>
            {t(`tasksQuestions.${firstQuestion.code}.modalCancel`)}
          </Button>
        </ButtonsContainer>

        {isAnswerError && (
          <ErrorContainer>
            <Text
              as="div"
              variant="label"
              sx={{ color: baseColors.red, fontWeight: 'bold', mt: 4 }}
            >
              {t('error.TryAgain')}
            </Text>
          </ErrorContainer>
        )}
      </WhiteBox>
    </Modal>
  )
}
